import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import {
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonInput,
  IonIcon,
  IonButton,
  IonAlert,
  IonToggle,
  IonLabel,
} from "@ionic/react";
import {
  searchOutline,
  closeCircleOutline,
  downloadOutline,
  personAddOutline,
} from "ionicons/icons";
import { CSVLink } from "react-csv";
import { Spinner } from "pymble-ui/components";
import { displayMsg } from "pymble-ui/components";
import { useGroupMembers } from "../../hooks/useGroupMembers";
import { useSelectMemberPopover } from "../../hooks/useSelectMemberPopover";
import MemberCard from "./MemberCard";
import { isSubscriber } from "./memberSections";

import "./MembersTab.css";

const PAGE_SIZE = 60;

interface MembersTabProps {
  groupId: string;
  isAdmin?: boolean;
}

const MembersTab: React.FC<MembersTabProps> = ({
  groupId,
  isAdmin = false,
}) => {
  const [showExpired, setShowExpired] = useState(false);

  const {
    groupMembers,
    groupMembersFiltered,
    groupMembersLoaded,
    groupSubscribers,
    groupSubscribersFiltered,
    setGroupMembersFilter,
    setGroupSubscribersFilter,
    addGroupMember,
    saveGroupMember,
    deleteGroupMember,
  } = useGroupMembers(groupId, isAdmin && showExpired);

  // Both sections feed the add-member popover's "already in this group"
  // check. Passing only the members list would offer a subscriber as an
  // addable contact and the POST would come back 409.
  const everyMember = useMemo(
    () =>
      [...groupMembers, ...groupSubscribers] as {
        _id: string;
        ContactIdLookup?: string;
        [key: string]: unknown;
      }[],
    [groupMembers, groupSubscribers]
  );

  const { presentContactPopover, selectedMember, MemberModal } =
    useSelectMemberPopover(everyMember);

  // Add member when selected from the search modal
  useEffect(() => {
    if (selectedMember?._id) {
      (async () => {
        try {
          await addGroupMember(selectedMember._id);
          displayMsg("Member added", {
            type: "success",
            position: "top",
            duration: 1500,
          });
        } catch {
          displayMsg("Failed to add member", {
            type: "danger",
            position: "top",
            duration: 3000,
          });
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember]);

  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef<HTMLIonInputElement>(null);
  const [memberToDelete, setMemberToDelete] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [visibleSubscriberCount, setVisibleSubscriberCount] =
    useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Reset pagination when search changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setVisibleSubscriberCount(PAGE_SIZE);
  }, [searchText]);

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setGroupMembersFilter({ search: value });
    setGroupSubscribersFilter({ search: value });
  };

  const visibleMembers = useMemo(
    () => groupMembersFiltered.slice(0, visibleCount),
    [groupMembersFiltered, visibleCount]
  );

  const visibleSubscribers = useMemo(
    () => groupSubscribersFiltered.slice(0, visibleSubscriberCount),
    [groupSubscribersFiltered, visibleSubscriberCount]
  );

  const hasMoreSubscribers =
    visibleSubscriberCount < groupSubscribersFiltered.length;

  const hasMore = visibleCount < groupMembersFiltered.length;

  const loadMore = useCallback(() => {
    setVisibleCount((prev) =>
      Math.min(prev + PAGE_SIZE, groupMembersFiltered.length)
    );
  }, [groupMembersFiltered.length]);

  // Infinite scroll
  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "200px" }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  // CSV data for export. Both sections, with the column that says which is
  // which - the export used to be the members list alone, so subscribers
  // were missing from it as silently as they were missing from the screen.
  const csvData = useMemo(() => {
    return [...groupMembersFiltered, ...groupSubscribersFiltered].map(
      (m: {
        MemberName?: string;
        RoleLabel?: string;
        Permission?: { Admin?: boolean };
        Subscribed?: boolean;
      }) => ({
        Name: m.MemberName || "",
        Role: m.RoleLabel || "Member",
        Admin: m.Permission?.Admin ? "Yes" : "No",
        Subscriber: isSubscriber(m) ? "Yes" : "No",
      })
    );
  }, [groupMembersFiltered, groupSubscribersFiltered]);

  const handleSaveMember = async (member: {
    _id: string;
    RoleLabel: string;
    Permission: { Admin: boolean };
    Settings: { Notify: boolean; Calendar: boolean };
    Subscribed?: boolean;
  }) => {
    try {
      await saveGroupMember(member);
      displayMsg(
        member.Subscribed === false ? "Member promoted" : "Member updated",
        {
          type: "success",
          position: "top",
          duration: 1500,
        }
      );
    } catch {
      displayMsg("Failed to update member", {
        type: "danger",
        position: "top",
        duration: 3000,
      });
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToDelete) return;
    try {
      await deleteGroupMember(memberToDelete);
      displayMsg("Member removed", {
        type: "success",
        position: "top",
        duration: 1500,
      });
    } catch {
      displayMsg("Failed to remove member", {
        type: "danger",
        position: "top",
        duration: 3000,
      });
    }
    setMemberToDelete(null);
  };

  if (!groupMembersLoaded) {
    return <Spinner message="Loading members..." />;
  }

  return (
    <div className="members-tab">
      <IonGrid className="members-toolbar">
        <IonRow className="ion-align-items-center">
          <IonCol size="12" sizeMd="3">
            <IonItem lines="none" className="member-search-item">
              <IonIcon slot="start" icon={searchOutline} color="medium" />
              <IonInput
                ref={searchInputRef}
                placeholder="Search members..."
                debounce={300}
                value={searchText}
                onIonInput={(e) =>
                  handleSearchChange(e.detail.value || "")
                }
              />
              {searchText && (
                <IonIcon
                  icon={closeCircleOutline}
                  slot="end"
                  color="medium"
                  onClick={() => {
                    handleSearchChange("");
                    if (searchInputRef.current)
                      searchInputRef.current.value = "";
                  }}
                />
              )}
            </IonItem>
          </IonCol>
          <IonCol size="12" sizeMd="9" className="members-actions">
            <span className="member-count">
              Showing {visibleMembers.length} of{" "}
              {groupMembersFiltered.length} member
              {groupMembersFiltered.length !== 1 ? "s" : ""}
              {groupSubscribersFiltered.length > 0 &&
                ` · ${groupSubscribersFiltered.length} subscriber${
                  groupSubscribersFiltered.length !== 1 ? "s" : ""
                }`}
            </span>
            {isAdmin && (
              <IonItem lines="none" className="show-expired-toggle">
                <IonLabel>Show Expired</IonLabel>
                <IonToggle
                  checked={showExpired}
                  onIonChange={() => setShowExpired(!showExpired)}
                  color="secondary"
                />
              </IonItem>
            )}
            {isAdmin && (
              <IonButton
                fill="solid"
                color="secondary"
                size="small"
                className="ion-text-capitalize"
                onClick={() => presentContactPopover()}
              >
                <IonIcon slot="start" icon={personAddOutline} />
                Add Member
              </IonButton>
            )}
            <CSVLink
              data={csvData}
              filename="members.csv"
              className="csv-export-link"
            >
              <IonButton
                fill="outline"
                color="medium"
                size="small"
                className="ion-text-capitalize"
              >
                <IonIcon slot="start" icon={downloadOutline} />
                Export CSV
              </IonButton>
            </CSVLink>
          </IonCol>
        </IonRow>
      </IonGrid>

      {groupMembersFiltered.length === 0 &&
      groupSubscribersFiltered.length === 0 ? (
        <p className="members-empty">
          {searchText
            ? "No members match your search."
            : "This group has no members."}
        </p>
      ) : (
        <>
          <div className="members-list">
            {visibleMembers.map(
              (member: {
                _id: string;
                MemberName?: string;
                RoleLabel?: string;
                ContactPersonalId?: string;
                Permission?: { Admin?: boolean };
                [key: string]: unknown;
              }) => (
                <MemberCard
                  key={member._id}
                  member={member}
                  isAdmin={isAdmin}
                  onRemove={(id) => setMemberToDelete(id)}
                  onSave={isAdmin ? handleSaveMember : undefined}
                />
              )
            )}
          </div>
          {hasMore && (
            <div className="members-load-more" ref={sentinelRef}>
              <IonButton
                fill="outline"
                color="medium"
                size="small"
                className="ion-text-capitalize"
                onClick={loadMore}
              >
                Load More ({groupMembersFiltered.length - visibleCount}{" "}
                remaining)
              </IonButton>
            </div>
          )}

          {groupSubscribersFiltered.length > 0 && (
            <div className="members-subscribers">
              <h3 className="members-section-heading">
                Subscribers ({groupSubscribersFiltered.length})
              </h3>
              <div className="members-list">
                {visibleSubscribers.map(
                  (member: {
                    _id: string;
                    MemberName?: string;
                    RoleLabel?: string;
                    ContactPersonalId?: string;
                    Permission?: { Admin?: boolean };
                    [key: string]: unknown;
                  }) => (
                    <MemberCard
                      key={member._id}
                      member={member}
                      isAdmin={isAdmin}
                      onRemove={(id) => setMemberToDelete(id)}
                      onSave={isAdmin ? handleSaveMember : undefined}
                    />
                  )
                )}
              </div>
              {hasMoreSubscribers && (
                <div className="members-load-more">
                  <IonButton
                    fill="outline"
                    color="medium"
                    size="small"
                    className="ion-text-capitalize"
                    onClick={() =>
                      setVisibleSubscriberCount((prev) =>
                        Math.min(
                          prev + PAGE_SIZE,
                          groupSubscribersFiltered.length
                        )
                      )
                    }
                  >
                    Load More (
                    {groupSubscribersFiltered.length - visibleSubscriberCount}{" "}
                    remaining)
                  </IonButton>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <IonAlert
        isOpen={!!memberToDelete}
        onDidDismiss={() => setMemberToDelete(null)}
        header="Remove Member"
        message="Are you sure you want to remove this member from the group?"
        buttons={[
          { text: "Cancel", role: "cancel" },
          {
            text: "Remove",
            role: "destructive",
            handler: handleRemoveMember,
          },
        ]}
      />

      {isAdmin && MemberModal}
    </div>
  );
};

export default MembersTab;
